// Requested acknowledgements on priority messages (shared/API.md §41.3) — the pure rules. No twin
// existed when this was written (the apps only knew the `com.satoritech.acknowledgements` plugin's
// forms, shared/PLUGINS.md §8.2); this is the contract the five clients implement together.
//
// Server facts, read from the Mattermost 12 source (2026-10-18):
// - `metadata.priority = {priority, requested_ack, persistent_notifications}` (model.PostPriority).
//   `postPriorityCheck` (app/post_permission_utils.go): priority only on ROOT posts (400 on a reply);
//   `requested_ack` / `persistent_notifications` need a Professional licence (501 otherwise);
//   persistent needs `AllowPersistentNotifications` and priority `urgent`.
// - The client config carries `PostAcknowledgements: "true"` ONLY with that licence
//   (config/client.go), so it is the server's own "may I offer this" answer.
// - `POST|DELETE /users/{me}/posts/{id}/ack`; the delete is refused 5 minutes after the ack
//   (app/post_acknowledgements.go: `GetMillis() - AcknowledgedAt > 5*60*1000`).
// - Events `post_acknowledgement_added|_removed`, broadcast to the channel, `data.acknowledgement`
//   = the JSON-encoded PostAcknowledgement `{user_id, post_id, acknowledged_at, channel_id}`.
import type { Level } from './messagePriority';

export interface Acknowledgement {
  userId: string;
  postId: string;
  acknowledgedAt: number;
}

/** What a post asked for, decoded from `metadata.priority`. */
export interface AckRequest {
  requestedAck: boolean;
  persistent: boolean;
}

/** The composer's choices beyond the level. */
export interface AckChoice {
  requestAck: boolean;
  persistent: boolean;
}

/** The wire `metadata.priority`, or null when the post is standard and asks for nothing. */
export type PriorityWire = { priority: '' | Level; requested_ack?: boolean; persistent_notifications?: boolean };

/** The server's own undo window. */
export const UNDO_WINDOW_MS = 5 * 60 * 1000;

/** The composer may offer «Solicitar confirmación»: the server says so, and only on a root post. */
export function canRequest(clientConfig: { postAcknowledgements: boolean }, rootId: string | null | undefined): boolean {
  return clientConfig.postAcknowledgements && !rootId;
}

/** «Notificar persistentemente»: urgent only, the server allows it, root only (a licensed server). */
export function canPersist(
  clientConfig: { postAcknowledgements: boolean; persistentNotifications: boolean },
  level: Level | null,
  rootId: string | null | undefined,
): boolean {
  return canRequest(clientConfig, rootId) && clientConfig.persistentNotifications && level === 'urgent';
}

/**
 * The priority object a send carries. A standard message that asks for confirmation still sends
 * `priority: ""` (Mattermost's own shape for "standard, with an ack request"); persistent rides
 * only with urgent — anything else would be refused by the server with a 400.
 */
export function wire(level: Level | null, choice: AckChoice): PriorityWire | null {
  const persistent = choice.persistent && level === 'urgent';
  if (!level && !choice.requestAck) return null;
  const out: PriorityWire = { priority: level ?? '' };
  if (choice.requestAck) out.requested_ack = true;
  if (persistent) out.persistent_notifications = true;
  return out;
}

type RawPriority = { priority?: unknown; requested_ack?: unknown; persistent_notifications?: unknown } | null | undefined;
type RawAck = { user_id?: unknown; post_id?: unknown; acknowledged_at?: unknown };

/** `metadata.priority` → what the post asked for (absent = nothing). */
export function decodeRequest(priority: RawPriority): AckRequest {
  return { requestedAck: priority?.requested_ack === true, persistent: priority?.persistent_notifications === true };
}

function decodeOne(raw: unknown, postId?: string, allowZero = false): Acknowledgement | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as RawAck;
  const userId = typeof a.user_id === 'string' ? a.user_id : '';
  const pid = typeof a.post_id === 'string' && a.post_id ? a.post_id : postId ?? '';
  const at = typeof a.acknowledged_at === 'number' ? a.acknowledged_at : Number(a.acknowledged_at);
  // acknowledged_at = 0 is how the server marks a removed ack in some payloads — not an ack.
  if (!userId || !pid) return null;
  if (!Number.isFinite(at) || at < 0 || (at === 0 && !allowZero)) return null;
  return { userId, postId: pid, acknowledgedAt: at };
}

/**
 * `metadata.acknowledgements` → the list, or null when the key is ABSENT (a payload without it says
 * nothing about acks; an empty array says "none"). One entry per person, earliest first.
 */
export function decodeList(raw: unknown, postId: string): Acknowledgement[] | null {
  if (raw === undefined) return null;
  if (!Array.isArray(raw)) return [];
  const byUser = new Map<string, Acknowledgement>();
  for (const item of raw) {
    const a = decodeOne(item, postId);
    if (a && a.postId === postId && !byUser.has(a.userId)) byUser.set(a.userId, a);
  }
  return sortAcks([...byUser.values()]);
}

/**
 * What one stored payload says about a post's acks. The server fills `metadata.acknowledgements`
 * together with `metadata.priority` (app/post_metadata.go) and omits an EMPTY list (`omitempty`), so:
 * priority present → the list is the truth (absent = nobody); priority absent → this payload was
 * prepared without priority data and says nothing (null — keep what is stored).
 */
export function fromMetadata(metadata: { priority?: unknown; acknowledgements?: unknown } | null | undefined, postId: string): { request: AckRequest; acks: Acknowledgement[] } | null {
  if (!metadata || metadata.priority == null || typeof metadata.priority !== 'object') return null;
  return { request: decodeRequest(metadata.priority as RawPriority), acks: decodeList(metadata.acknowledgements, postId) ?? [] };
}

/**
 * The event's `data.acknowledgement` (a JSON string, or an object from a lenient sender). A REMOVED
 * ack may arrive with `acknowledged_at: 0` (the store's delete zeroes it), so 0 is accepted here —
 * the event name, not the time, says which way it went.
 */
export function decodeEvent(raw: unknown): Acknowledgement | null {
  if (typeof raw === 'string') {
    try {
      return decodeOne(JSON.parse(raw), undefined, true);
    } catch {
      return null;
    }
  }
  return decodeOne(raw, undefined, true);
}

function sortAcks(list: Acknowledgement[]): Acknowledgement[] {
  return list.sort((a, b) => a.acknowledgedAt - b.acknowledgedAt || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0));
}

/** An added / removed event applied to the stored list. */
export function apply(list: readonly Acknowledgement[], ack: Acknowledgement, added: boolean): Acknowledgement[] {
  const rest = list.filter((a) => a.userId !== ack.userId);
  return added ? sortAcks([...rest, ack]) : rest;
}

export interface AckSummary {
  count: number;
  /** The reader's own ack, if any. */
  mine: Acknowledgement | null;
  /** «Confirmar» is offered: someone else's post, not yet confirmed by me. */
  offersConfirm: boolean;
  /** My ack can still be taken back (the server's 5-minute window). */
  canUndo: boolean;
}

export function summary(list: readonly Acknowledgement[], me: string, isOwnPost: boolean, now: number): AckSummary {
  const mine = list.find((a) => a.userId === me) ?? null;
  return {
    count: list.length,
    mine,
    offersConfirm: !isOwnPost && !mine,
    canUndo: !!mine && now - mine.acknowledgedAt <= UNDO_WINDOW_MS,
  };
}

/** The count's string key and argument: «1 confirmación» / «N confirmaciones» / «Sin confirmaciones». */
export function countLabel(count: number): { key: string; arg?: number } {
  if (count <= 0) return { key: 'web.ack.count.none' };
  if (count === 1) return { key: 'web.ack.count.one' };
  return { key: 'web.ack.count.many', arg: count };
}

export const PostAcknowledgements = { canRequest, canPersist, wire, decodeRequest, decodeList, fromMetadata, decodeEvent, apply, summary, countLabel, UNDO_WINDOW_MS } as const;
